import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getConfig } from '@edx/frontend-platform';
import { AppContext } from '@edx/frontend-platform/react';
import {
  Spinner, Alert, Badge, Button, Icon, IconButton,
} from '@openedx/paragon';
import {
  RiArrowLeftLine,
  RiCalendarLine,
  RiTimeLine,
  RiExternalLinkLine,
  RiVideoLine,
  RiFileCopyLine,
  RiBook3Line,
} from '@remixicon/react';

import { useIntl } from '@edx/frontend-platform/i18n';

import { useLiveSessionById, useLiveVideoRecording } from '../../gql/hooks/useLiveSessions';
import { fetchLiveSessionJoinUrl } from '../../utils/liveSessionJoin';
import livePlayBtn from '../../assets/live-play-btn.png';
import messages from './messages';
import './LiveSessions.scss';

const useStatusText = () => {
  const intl = useIntl();
  return (status) => {
    if (status === 'live') return intl.formatMessage(messages.statusLive);
    if (status === 'ended') return intl.formatMessage(messages.statusEnded);
    return intl.formatMessage(messages.statusScheduled);
  };
};

const useFormatDuration = () => {
  const intl = useIntl();
  return (mins) => {
    if (!mins) return null;
    if (mins < 60) return intl.formatMessage(messages.durationMinutes, { minutes: mins });
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (m) return intl.formatMessage(messages.durationHoursMinutes, { hours: h, minutes: m });
    return intl.formatMessage(messages.durationHours, { hours: h });
  };
};

const RecordingCard = ({ recording, index }) => {
  const intl = useIntl();
  const video = recording?.videoLibrary;
  const [imageError, setImageError] = useState(false);
  if (!video) return null;
  const seconds = video.metadata?.length ?? 0;
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n) => String(n).padStart(2, '0');
  const duration = h > 0 ? `${pad(h)}:${pad(m % 60)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  const link = video.mp4Link || video.hlsLink;
  const title = video.title || intl.formatMessage(messages.recordingFallbackTitle, { index });
  const playable = !!link;

  const copyLink = (e) => {
    e.preventDefault();
    if (link) navigator.clipboard.writeText(link);
  };
  const openLink = (e) => {
    e.preventDefault();
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
  };

  const showFallback = imageError || !video.thumbnail;

  return (
    <div className="recording-card">
      <div className="recording-card__thumb">
        {!showFallback && (
          <img
            src={video.thumbnail}
            alt={title}
            className="recording-card__thumb-img"
            onError={() => setImageError(true)}
          />
        )}
        {showFallback && <Icon src={RiVideoLine} size="lg" />}
      </div>
      <div className="recording-card__body">
        <div className="recording-card__title" title={title}>{title}</div>
        <div className="recording-card__duration">
          <Icon src={RiTimeLine} size="xs" />
          {duration}
        </div>
        <div className="recording-card__actions">
          <IconButton
            src={RiFileCopyLine}
            iconAs={Icon}
            alt={intl.formatMessage(messages.copyLink)}
            variant="outline-secondary"
            size="sm"
            onClick={copyLink}
            disabled={!playable}
          />
          <Button
            variant="outline-primary"
            size="sm"
            onClick={openLink}
            disabled={!playable}
            iconBefore={RiExternalLinkLine}
          >
            {intl.formatMessage(messages.openInNewTab)}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RelatedCourseCard = ({ course }) => {
  const intl = useIntl();
  const overview = course?._CourseOverviewsCourseoverview;
  if (!overview) return null;
  const courseId = course?.openedxCourseId;
  const homeUrl = courseId ? `${getConfig().LMS_BASE_URL}/courses/${courseId}/home` : null;
  const imageUrl = overview.courseImageUrl
    ? `${getConfig().LMS_BASE_URL}${overview.courseImageUrl}`
    : null;
  const startDate = overview.startDate
    ? format(new Date(overview.startDate), 'MMM dd, yyyy')
    : null;

  return (
    <section className="live-session-detail__section">
      <h2>{intl.formatMessage(messages.partOfCourse)}</h2>
      <p className="live-session-detail__section-sub">
        {intl.formatMessage(messages.partOfCourseDescription)}
      </p>
      <a
        className="live-session-detail__related-course"
        href={homeUrl || '#'}
        {...(homeUrl ? {} : { 'aria-disabled': 'true', tabIndex: -1 })}
      >
        <div
          className="live-session-detail__related-course-image"
          style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
          role="img"
          aria-label={overview.displayName}
        >
          <div className="live-session-detail__related-course-badge">
            <Icon src={RiBook3Line} size="inline" />
            <span>{intl.formatMessage(messages.courseBadgeLabel)}</span>
          </div>
        </div>
        <div className="live-session-detail__related-course-body">
          <div className="live-session-detail__related-course-title">{overview.displayName}</div>
          {startDate && (
            <div className="live-session-detail__related-course-meta">
              <Icon src={RiCalendarLine} size="xs" />
              {intl.formatMessage(messages.startsOn, { date: startDate })}
            </div>
          )}
        </div>
      </a>
    </section>
  );
};

const LiveSessionDetail = () => {
  const intl = useIntl();
  const statusText = useStatusText();
  const formatDuration = useFormatDuration();
  const { sessionId } = useParams();
  const { authenticatedUser } = useContext(AppContext);
  const { session, loading, error } = useLiveSessionById(sessionId);
  const { recordings } = useLiveVideoRecording(sessionId);

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [joinUrl, setJoinUrl] = useState(null);

  const sessionData = useMemo(() => {
    if (!session) return null;
    const startTime = session.startTime ? new Date(session.startTime) : null;
    const endTime = startTime && session.duration
      ? new Date(startTime.getTime() + session.duration * 60 * 1000)
      : null;
    let timeRange = null;
    if (startTime) {
      const startStr = format(startTime, 'h:mm a');
      timeRange = endTime ? `${startStr} - ${format(endTime, 'h:mm a')}` : startStr;
    }
    let tz = 'UTC';
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').pop().replace(/_/g, ' ');
    } catch (_) { /* ignore */ }
    return {
      ...session,
      startTime,
      endTime,
      isLive: session.status === 'live',
      isScheduled: session.status === 'scheduled',
      isEnded: session.status === 'ended',
      dateDisplay: startTime ? format(startTime, 'EEEE, MMMM dd, yyyy') : null,
      timeRange,
      timezone: tz,
    };
  }, [session]);

  useEffect(() => {
    if (!sessionData?.isLive || joinUrl) return undefined;
    let cancelled = false;
    setJoining(true);
    setJoinError(null);
    (async () => {
      try {
        const url = await fetchLiveSessionJoinUrl(sessionId, authenticatedUser);
        if (cancelled) return;
        if (url) setJoinUrl(url);
        else setJoinError(intl.formatMessage(messages.joinUrlUnavailable));
      } catch (e) {
        if (!cancelled) setJoinError(e?.response?.data?.error || intl.formatMessage(messages.joinSessionFailed));
      } finally {
        if (!cancelled) setJoining(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionData?.isLive, joinUrl, sessionId, authenticatedUser]);

  if (loading) {
    return (
      <div className="live-sessions-page__state">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages.detailLoading)} />
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="live-session-detail">
        <Alert variant="danger">
          {error
            ? intl.formatMessage(messages.detailLoadError)
            : intl.formatMessage(messages.detailNotFound)}
        </Alert>
      </div>
    );
  }

  const durationDisplay = formatDuration(sessionData.duration);
  const scheduledTimeDisplay = sessionData.timeRange
    ? `${sessionData.timeRange}${sessionData.timezone ? ` (${sessionData.timezone})` : ''}`
    : null;
  const relatedCourse = sessionData.liveSessionCourses?.[0]?.course;
  const providerName = sessionData.liveSessionProvider?.name;

  const firstRecordingLink = recordings?.[0]?.videoLibrary?.mp4Link
    || recordings?.[0]?.videoLibrary?.hlsLink
    || null;
  const showHeroPlay = sessionData.isLive || (sessionData.isEnded && firstRecordingLink);
  const handleHeroPlay = async () => {
    if (sessionData.isLive) {
      let url = joinUrl;
      if (!url) {
        try {
          url = await fetchLiveSessionJoinUrl(sessionId, authenticatedUser);
          if (url) setJoinUrl(url);
        } catch (e) {
          setJoinError(e?.response?.data?.error || intl.formatMessage(messages.openSessionFailed));
          return;
        }
      }
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } else if (firstRecordingLink) {
      window.open(firstRecordingLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="live-session-detail">
      <Link to="/live-sessions" className="live-session-detail__back">
        <Icon src={RiArrowLeftLine} size="xs" />
        {intl.formatMessage(messages.backToLiveSessions)}
      </Link>

      <div
        className="live-session-detail__hero"
        style={(!sessionData.isLive || !joinUrl) && sessionData.cardImageUrl
          ? { backgroundImage: `url(${sessionData.cardImageUrl})` }
          : undefined}
      >
        {sessionData.isLive && joinUrl ? (
          <iframe
            src={joinUrl}
            title={intl.formatMessage(messages.liveSessionFrameTitle)}
            allow="microphone *; camera *; midi *; geolocation *; encrypted-media *; display-capture *; autoplay *; clipboard-write *"
            allowFullScreen
            referrerPolicy="origin"
            className="live-session-detail__hero-iframe"
          />
        ) : (
          <div className="live-session-detail__hero-overlay" />
        )}
        <div className="live-session-detail__hero-top">
          <h1 className="live-session-detail__hero-title">{sessionData.title}</h1>
          <Badge variant="solid" color="gray" size="md">
            {statusText(sessionData.status)}
          </Badge>
        </div>
        {showHeroPlay && (
          <button
            type="button"
            className="live-session-detail__hero-play"
            onClick={handleHeroPlay}
            disabled={joining}
            aria-label={sessionData.isLive
              ? intl.formatMessage(messages.openLiveInNewTab)
              : intl.formatMessage(messages.playRecording)}
          >
            <img src={livePlayBtn} alt="" />
          </button>
        )}
      </div>

      {joinError && sessionData.isLive && (
        <Alert variant="danger">{joinError}</Alert>
      )}

      {sessionData.description && (
        <section className="live-session-detail__info-card">
          <h2 className="live-session-detail__info-card-title">
            {intl.formatMessage(messages.aboutSession)}
          </h2>
          <p className="live-session-detail__info-card-text">{sessionData.description}</p>
        </section>
      )}

      <section className="live-session-detail__info-card">
        <h2 className="live-session-detail__info-card-title">
          {intl.formatMessage(messages.sessionDetails)}
        </h2>
        <div className="live-session-detail__details">
          {(sessionData.dateDisplay || scheduledTimeDisplay) && (
            <div className="live-session-detail__details-row">
              <div className="live-session-detail__details-cell">
                <span className="live-session-detail__details-label">
                  {intl.formatMessage(messages.scheduledDateLabel)}
                </span>
                <span className="live-session-detail__details-value">
                  {sessionData.dateDisplay || '—'}
                </span>
              </div>
              <span className="live-session-detail__details-divider" aria-hidden />
              <div className="live-session-detail__details-cell">
                <span className="live-session-detail__details-label">
                  {intl.formatMessage(messages.scheduledTimeLabel)}
                </span>
                <span className="live-session-detail__details-value">
                  {scheduledTimeDisplay || '—'}
                </span>
              </div>
            </div>
          )}
          <div className="live-session-detail__details-row">
            <div className="live-session-detail__details-cell">
              <span className="live-session-detail__details-label">
                {intl.formatMessage(messages.statusLabel)}
              </span>
              <Badge variant="solid" color="gray" size="md">
                {statusText(sessionData.status)}
              </Badge>
            </div>
            <div className="live-session-detail__details-cell" aria-hidden />
          </div>
          {durationDisplay && (
            <div className="live-session-detail__details-row">
              <div className="live-session-detail__details-cell">
                <span className="live-session-detail__details-label">
                {intl.formatMessage(messages.estimatedDurationLabel)}
              </span>
                <span className="live-session-detail__details-value">{durationDisplay}</span>
              </div>
              <div className="live-session-detail__details-cell" aria-hidden />
            </div>
          )}
          {providerName && (
            <div className="live-session-detail__details-row">
              <div className="live-session-detail__details-cell">
                <span className="live-session-detail__details-label">
                {intl.formatMessage(messages.providerLabel)}
              </span>
                <span className="live-session-detail__details-value">{providerName}</span>
              </div>
              <div className="live-session-detail__details-cell" aria-hidden />
            </div>
          )}
        </div>
      </section>

      {relatedCourse && <RelatedCourseCard course={relatedCourse} />}

      {recordings.length > 0 && (
        <section className="live-session-detail__section">
          <h2>{intl.formatMessage(messages.recordingsHeading)}</h2>
          <p className="live-session-detail__section-sub">
            {intl.formatMessage(messages.recordingsAvailable, { count: recordings.length })}
          </p>
          <div className="live-session-detail__recordings">
            {recordings.map((r, i) => (
              <RecordingCard key={r.id} recording={r} index={i + 1} />
            ))}
          </div>
        </section>
      )}

      {sessionData.isEnded && recordings.length === 0 && (
        <section className="live-session-detail__section">
          <h2>{intl.formatMessage(messages.recordingsHeading)}</h2>
          <Alert variant="info">{intl.formatMessage(messages.noRecordings)}</Alert>
        </section>
      )}
    </div>
  );
};

export default LiveSessionDetail;
